// src/components/CustomerStorySection.js
import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  Drawer,
  IconButton,
  Button,
  Stack,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { describe } from "../utils/scoring";

/**
 * -------------------------
 * Small utilities
 * -------------------------
 */

function normalizeOptionalValue(val) {
  if (val === null || val === undefined) return "";
  const s = String(val).trim();
  if (!s) return "";
  if (s.toLowerCase() === "unknown") return "";
  return s;
}

function isNonEmpty(val) {
  return Boolean(normalizeOptionalValue(val));
}

function asArray(x) {
  return Array.isArray(x) ? x : [];
}

function safeNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

function timeToSeconds(str) {
  // supports mm:ss or hh:mm:ss
  const parts = String(str).trim().split(":").map((p) => p.trim());
  if (parts.length < 2 || parts.length > 3) return null;

  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n))) return null;

  if (parts.length === 2) {
    const [mm, ss] = nums;
    if (mm < 0 || ss < 0 || ss >= 60) return null;
    return mm * 60 + ss;
  }

  const [hh, mm, ss] = nums;
  if (hh < 0 || mm < 0 || ss < 0 || mm >= 60 || ss >= 60) return null;
  return hh * 3600 + mm * 60 + ss;
}

function extractJumpPoints(timeRangesOrString) {
  if (!timeRangesOrString) return [];

  // ✅ New canonical format: array of { start_time, end_time }
  if (Array.isArray(timeRangesOrString)) {
    const points = [];

    for (const r of timeRangesOrString) {
      const start = r?.start_time;
      const end = r?.end_time;

      const startSec = timeToSeconds(start);
      const endSec = timeToSeconds(end);

      if (startSec === null || endSec === null) continue;

      // label is the range, jump is to the start
      points.push({
        label: `${start}–${end}`,
        seconds: startSec,
      });
    }

    // de-dupe by seconds
    const seen = new Set();
    return points.filter((p) => {
      if (seen.has(p.seconds)) return false;
      seen.add(p.seconds);
      return true;
    });
  }

  // ⚠️ Legacy support (optional): parse strings the old way (pre-2026)
  const s = String(timeRangesOrString);
  const matches = s.match(/(\d{1,2}:\d{2}(?::\d{2})?)/g) || [];

  const points = [];
  for (const m of matches) {
    const sec = timeToSeconds(m);
    if (sec === null) continue;
    points.push({ label: m, seconds: sec });
  }

  const seen = new Set();
  return points.filter((p) => {
    if (seen.has(p.seconds)) return false;
    seen.add(p.seconds);
    return true;
  });
}

/**
 * -------------------------
 * Metric pill w/ colored scale (no colored text)
 * -------------------------
 */

function gradientStopsForScheme(scheme) {
  // low -> high (bar shows more of the high end as it fills)
  if (scheme === "red") return ["#f7b6b6", "#c62828"];
  if (scheme === "blue") return ["#b3d4ff", "#1565c0"];
  // default green
  return ["#bfe7c6", "#2e7d32"];
}

function MetricScalePill({
  label,
  value,
  metricKey,
  scheme = "green",
  showOutOfTen = false,
}) {
  const n = safeNumber(value);
  if (n === null) return null;

  const rounded = Math.round(n);

  // Insights: still map to the underlying scoring.js key so qualifiers work
  const metricKeyForDescribe =
    metricKey === "sentiment" || metricKey === "intensity"
      ? "insight_sentiment_score"
      : metricKey;

  const qual = metricKeyForDescribe ? describe(metricKeyForDescribe, rounded) : "";

  const [low, high] = gradientStopsForScheme(scheme);

  const raw = Number(value);
  const clamped = Number.isFinite(raw) ? Math.max(0, Math.min(10, raw)) : 0;
  const pct = (clamped / 10) * 100;

  const gradientCssString = `linear-gradient(90deg, ${low}, ${high})`;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.5,
        borderRadius: "999px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        maxWidth: "100%",

        // keep on one line
        flexWrap: "nowrap",
        minWidth: 0,

        // IMPORTANT: don't hide text (no truncation). If it truly can't fit,
        // it will overflow rather than ellipsize.
        overflow: "visible",
      }}
    >
      {/* Text: full label + full qualifier, smaller and NOT bold */}
      <Typography
        component="span"
        variant="body2"
        sx={{
          fontSize: 12.5,
          fontWeight: 400,
          lineHeight: 1.15,
          whiteSpace: "nowrap",
          minWidth: 0,
          flex: "1 1 auto",
        }}
      >
        {label}:{" "}
        <Typography
          component="span"
          variant="body2"
          sx={{ fontSize: 12.5, fontWeight: 400 }}
        >
          {showOutOfTen ? `${rounded}/10` : `${rounded}`}
        </Typography>
        {qual ? (
          <Typography
            component="span"
            variant="body2"
            sx={{ fontSize: 12.5, fontWeight: 400 }}
          >
            {" "}
            · {qual}
          </Typography>
        ) : null}
      </Typography>

      {/* Bar: keep but allow slightly smaller max so pills fit more often */}
      <Box
        sx={{
          position: "relative",
          height: 10,
          borderRadius: "999px",
          overflow: "hidden",
          bgcolor: "grey.200",

          // smaller max width than before to make room for full text
          width: "clamp(40px, 11vw, 86px)",
          flex: "0 0 auto",
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, background: gradientCssString }} />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${pct}%`,
            width: `calc(100% - ${pct}%)`,
            bgcolor: "grey.200",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "999px",
            border: "1px solid",
            borderColor: "divider",
            pointerEvents: "none",
          }}
        />
      </Box>
    </Box>
  );
}


function insightSchemeFromType(insightType, bucketFallback) {
  const t = normalizeOptionalValue(insightType);
  const b = normalizeOptionalValue(bucketFallback);

  const key = t || b;
  if (key === "Negatives") return "red";
  if (key === "Feature Request" || key === "Wish List") return "blue";
  // Positives + Misc default green
  return "green";
}

/**
 * -------------------------
 * Section / layout primitives
 * -------------------------
 */

function SectionTitle({ title, subtitle }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function SubsectionTitle({ title, subtitle }) {
  return (
    <Box sx={{ mt: 2.5, mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function TileGrid({ children, columns = { xs: 1, sm: 2, md: 3 } }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: {
          xs: `repeat(${columns.xs}, minmax(0, 1fr))`,
          sm: `repeat(${columns.sm}, minmax(0, 1fr))`,
          md: `repeat(${columns.md}, minmax(0, 1fr))`,
        },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Summary tile:
 * - single link-out icon ONLY (fixes double-icon bug)
 * - icon sits on the right of the metrics row
 */
function SummaryTile({
  title,
  chips = [],
  metrics = [],
  onClick,
  lineClamp = 2,
  minLinesHeight = 36,
}) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 2,
        cursor: "pointer",
        transition: "transform 120ms ease, box-shadow 120ms ease",
        "&:hover": { transform: "translateY(-1px)", boxShadow: 2 },
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 900,
          lineHeight: 1.25,
          display: "-webkit-box",
          WebkitLineClamp: lineClamp,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          minHeight: minLinesHeight,
        }}
        title={title || ""}
      >
        {title || "—"}
      </Typography>

      <Box
        sx={{
          mt: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minWidth: 0 }}>
          {metrics.filter(Boolean)}
          {chips.filter(Boolean).slice(0, 3)}
          {chips.filter(Boolean).length > 3 ? (
            <Chip size="small" variant="outlined" label={`+${chips.filter(Boolean).length - 3}`} />
          ) : null}
        </Box>

        <Tooltip title="Open details">
          <OpenInNewIcon sx={{ fontSize: 18, color: "text.secondary", flex: "0 0 auto" }} />
        </Tooltip>
      </Box>
    </Paper>
  );
}

/**
 * -------------------------
 * Drawer content rendering
 * -------------------------
 */

function DrawerField({ label, value, multiline = true }) {
  const v = normalizeOptionalValue(value);
  if (!v) return null;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: multiline ? "pre-wrap" : "normal" }}>
        {v}
      </Typography>
    </Box>
  );
}

function QuoteBlock({ quote, timestamps, hasVideo, onJumpToSeconds }) {
  const q = normalizeOptionalValue(quote);
  const ts = timestamps; // keep raw value (string OR array)
  if (!q && !ts) return null;

  const points = extractJumpPoints(timestamps);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
        Quote
      </Typography>

      {q ? (
        <Paper
          variant="outlined"
          sx={{
            mt: 0.75,
            p: 1.25,
            borderRadius: 2,
            bgcolor: "background.default",
          }}
        >
          <Typography variant="body2" sx={{ fontStyle: "italic", whiteSpace: "pre-wrap" }}>
            “{q}”
          </Typography>

          {hasVideo && points.length > 0 ? (
            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {points.map((p) => (
                <Chip
                  key={`${p.label}-${p.seconds}`}
                  size="small"
                  label={`▶ ${p.label}`}
                  onClick={() => onJumpToSeconds?.(p.seconds)}
                />
              ))}
            </Box>
          ) : ts ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Timestamp: {Array.isArray(ts) ? ts.map(r => `${r.start_time}–${r.end_time}`).join("; ") : ts}
            </Typography>
          ) : null}
        </Paper>
      ) : ts ? (
        <Typography variant="body2" sx={{ mt: 0.75 }}>
          Timestamp: {Array.isArray(ts) ? ts.map(r => `${r.start_time}–${r.end_time}`).join("; ") : ts}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * -------------------------
 * Tile normalizers
 * -------------------------
 */

function buildTiles(story) {
  const tiles = {
    chosenSolution: [],
    positives: [],
    drawbacks: [],
    stillUsed: [],
    notUsed: [],
    previousAndTrigger: [],

    // Considered solutions (each has a "solutionTile" and "reasonTiles")
    consideredSolutions: [],

    jobs: [],
    insights: [],
  };

  // --- Chosen solution
  if (isNonEmpty(story?.solution_choice) || isNonEmpty(story?.solution_choice_category)) {
    tiles.chosenSolution.push({
      id: "chosen-solution",
      kind: "chosen_solution",
      title: normalizeOptionalValue(story?.solution_choice) || "—",
      metrics: { satisfaction: safeNumber(story?.satisfaction_level) },
      detail: {
        solution_choice: story?.solution_choice,
        solution_choice_category: story?.solution_choice_category,
        satisfaction_level: story?.satisfaction_level,
        satisfaction_level_justification: story?.satisfaction_level_justification,
        satisfaction_level_quote: story?.satisfaction_level_quote,
        satisfaction_level_timestamps: story?.satisfaction_level_timestamps,
      },
    });
  }

  // --- Positives
  asArray(story?.positives_of_solution_choice).forEach((p, idx) => {
    if (!isNonEmpty(p?.positive) && !isNonEmpty(p?.positive_category)) return;

    tiles.positives.push({
      id: `positive-${idx}`,
      kind: "positive",
      title: normalizeOptionalValue(p?.positive) || "—",
      metrics: { importance: safeNumber(p?.positive_importance) },
      detail: { ...p },
    });
  });

  // --- Drawbacks
  asArray(story?.drawbacks_of_solution_choice).forEach((d, idx) => {
    if (!isNonEmpty(d?.drawback) && !isNonEmpty(d?.drawback_category)) return;

    tiles.drawbacks.push({
      id: `drawback-${idx}`,
      kind: "drawback",
      title: normalizeOptionalValue(d?.drawback) || "—",
      metrics: { importance: safeNumber(d?.drawback_importance) },
      detail: { ...d },
    });
  });

  // --- Still used situations
  asArray(story?.current_solution_still_used_in_these_situations).forEach((s, idx) => {
    if (!isNonEmpty(s?.situation) && !isNonEmpty(s?.situation_category)) return;

    tiles.stillUsed.push({
      id: `still-${idx}`,
      kind: "still_used",
      title: normalizeOptionalValue(s?.situation) || "—",
      metrics: {},
      detail: { ...s },
    });
  });

  // --- Not used situations
  asArray(story?.not_used_in_these_situations).forEach((s, idx) => {
    if (!isNonEmpty(s?.situation) && !isNonEmpty(s?.situation_category)) return;

    tiles.notUsed.push({
      id: `not-${idx}`,
      kind: "not_used",
      title: normalizeOptionalValue(s?.situation) || "—",
      metrics: {},
      detail: { ...s },
    });
  });

  // --- Previous solution & trigger
  const prev = normalizeOptionalValue(story?.previous_solution);
  const trig = normalizeOptionalValue(story?.trigger);

  if (prev || normalizeOptionalValue(story?.previous_solution_category)) {
    tiles.previousAndTrigger.push({
      id: "previous-solution",
      kind: "previous_solution",
      title: prev || "—",
      metrics: {},
      detail: {
        previous_solution: story?.previous_solution,
        previous_solution_category: story?.previous_solution_category,
        reasons_not_good_enough: story?.reasons_not_good_enough,
      },
    });
  }

  if (trig || normalizeOptionalValue(story?.trigger_category)) {
    tiles.previousAndTrigger.push({
      id: "trigger",
      kind: "trigger",
      title: trig || "—",
      metrics: {},
      detail: {
        trigger: story?.trigger,
        trigger_category: story?.trigger_category,
      },
    });
  }

  // --- Considered solutions (solution tile + reason tiles)
  asArray(story?.considered_solutions).forEach((cs, idx) => {
    const solutionTitle = normalizeOptionalValue(cs?.considered_solution) || "—";
    const reasons = asArray(cs?.reasons_not_chosen);

    const solutionTile = {
      id: `considered-solution-${idx}`,
      kind: "considered_solution",
      title: solutionTitle,
      metrics: {},
      detail: { ...cs },
    };

    const reasonTiles = reasons
      .map((r, rIdx) => {
        const rt = normalizeOptionalValue(r?.reason_not_chosen) || "—";
        if (!rt && !normalizeOptionalValue(r?.reason_not_chosen_category)) return null;
        return {
          id: `considered-${idx}-reason-${rIdx}`,
          kind: "not_chosen_reason",
          title: rt || "—",
          metrics: { importance: safeNumber(r?.reason_not_chosen_importance) },
          detail: {
            ...r,
            considered_solution: cs?.considered_solution,
            considered_solution_category: cs?.considered_solution_category,
          },
        };
      })
      .filter(Boolean);

    if (
      !normalizeOptionalValue(cs?.considered_solution) &&
      !normalizeOptionalValue(cs?.considered_solution_category) &&
      !reasonTiles.length
    ) {
      return;
    }

    tiles.consideredSolutions.push({ solutionTile, reasonTiles });
  });

  // --- Jobs to be done
  asArray(story?.jobs_to_be_done).forEach((j, idx) => {
    const jobCat = normalizeOptionalValue(j?.job_to_be_done_category);
    const title =
      jobCat ||
      normalizeOptionalValue(j?.job_to_be_done) ||
      normalizeOptionalValue(j?.raw_job_to_be_done) ||
      "—";

    const insightCount = asArray(j?.insights).length;

    tiles.jobs.push({
      id: `job-${idx}`,
      kind: "jtbd",
      title,
      metrics: {
        importance: safeNumber(j?.importance),
        satisfaction: safeNumber(j?.satisfaction),
      },
      chips: insightCount
        ? [<Chip key="ins" size="small" variant="outlined" label={`${insightCount} insight(s)`} />]
        : [],
      detail: { ...j },
    });
  });

  // --- Insights: merge job insights + miscellaneous_insights
  const combinedInsights = [];

  asArray(story?.jobs_to_be_done).forEach((j) => {
    asArray(j?.insights).forEach((ins) => combinedInsights.push({ ...ins }));
  });
  asArray(story?.miscellaneous_insights).forEach((ins) => combinedInsights.push({ ...ins }));

  combinedInsights.forEach((ins, idx) => {
    const title = normalizeOptionalValue(ins?.insight) || "—";
    if (!title && !normalizeOptionalValue(ins?.insight_category)) return;

    const area = normalizeOptionalValue(ins?.insight_product_area);

    tiles.insights.push({
      id: `insight-${idx}`,
      kind: "insight",
      title,
      metrics: { intensity: safeNumber(ins?.insight_sentiment_score) }, // rename sentiment -> intensity in UI
      chips: area ? [<Chip key="area" size="small" variant="outlined" label={area} />] : [],
      detail: { ...ins },
    });
  });

  return tiles;
}

/**
 * -------------------------
 * Drawer detail components
 * -------------------------
 */

function JtbdDetail({ d, hasVideo, onJumpToSeconds }) {
  const [tab, setTab] = useState(0); // 0 = Insights (default), 1 = Measures

  const measures = asArray(d?.how_they_measure_satisfaction);
  const insights = asArray(d?.insights);

  return (
    <>
      <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
        <MetricScalePill label="Importance" value={d?.importance} metricKey="importance" scheme="green" />
        <MetricScalePill label="Satisfaction" value={d?.satisfaction} metricKey="satisfaction" scheme="green" />
      </Box>

      <DrawerField label="Raw job to be done" value={d?.raw_job_to_be_done} />
      <DrawerField label="Situation category" value={d?.situation_category} multiline={false} />
      <DrawerField label="Job category" value={d?.job_to_be_done_category} multiline={false} />
      <DrawerField label="Desired outcome category" value={d?.desired_outcome_category} multiline={false} />

      <DrawerField label="Satisfaction justification" value={d?.satisfaction_justification} />
      <QuoteBlock
        quote={d?.satisfaction_quote}
        timestamps={d?.satisfaction_timestamps}
        hasVideo={hasVideo}
        onJumpToSeconds={onJumpToSeconds}
      />

      <Divider sx={{ my: 2 }} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="JTBD detail tabs">
        <Tab label="Insights" />
        <Tab label="Measures of satisfaction" />
      </Tabs>

      {tab === 0 ? (
        <Box sx={{ mt: 1.5 }}>
          {insights.length ? (
            <Stack spacing={1}>
              {insights.map((ins, idx) => {
                const scheme = insightSchemeFromType(ins?.insight_type);
                return (
                  <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                      <MetricScalePill
                        label="Intensity"
                        value={ins?.insight_sentiment_score}
                        metricKey="sentiment"
                        scheme={scheme}
                      />
                      {normalizeOptionalValue(ins?.insight_product_area) ? (
                        <Chip size="small" variant="outlined" label={normalizeOptionalValue(ins?.insight_product_area)} />
                      ) : null}
                    </Box>

                    <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.75 }}>
                      {normalizeOptionalValue(ins?.insight) || "—"}
                    </Typography>

                    <DrawerField label="Summary" value={ins?.insight_summary} />
                    <DrawerField label="Type" value={ins?.insight_type} multiline={false} />
                    <DrawerField label="Sentiment overall" value={ins?.insight_sentiment_overall} multiline={false} />
                    <DrawerField label="Sentiment expressed" value={ins?.insight_sentiment_expressed} multiline={false} />
                    <DrawerField label="Sentiment justification" value={ins?.insight_sentiment_justification} />

                    <QuoteBlock
                      quote={ins?.insight_quote}
                      timestamps={ins?.insight_timestamps}
                      hasVideo={hasVideo}
                      onJumpToSeconds={onJumpToSeconds}
                    />
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No insights were captured for this job.
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          {measures.length ? (
            <Stack spacing={1}>
              {measures.map((m, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {normalizeOptionalValue(m?.measure_of_satisfaction) || "—"}
                  </Typography>

                  <DrawerField
                    label="Measure category"
                    value={m?.measure_of_satisfaction_category}
                    multiline={false}
                  />
                  <DrawerField label="Justification" value={m?.measure_of_satisfaction_justification} />

                  <QuoteBlock
                    quote={m?.quote}
                    timestamps={m?.timestamp}
                    hasVideo={hasVideo}
                    onJumpToSeconds={onJumpToSeconds}
                  />
                </Paper>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No measures of satisfaction were captured for this job.
            </Typography>
          )}
        </Box>
      )}
    </>
  );
}

function DetailView({ tile, hasVideo, onJumpToSeconds }) {
  if (!tile) return null;
  const d = tile.detail || {};

  if (tile.kind === "chosen_solution") {
    return (
      <>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <MetricScalePill
            label="Satisfaction"
            value={d?.satisfaction_level}
            metricKey="satisfaction_level"
            scheme="green"
          />
        </Box>
        <DrawerField label="Solution choice category" value={d?.solution_choice_category} multiline={false} />
        <DrawerField label="Why this satisfaction score?" value={d?.satisfaction_level_justification} />
        <QuoteBlock
          quote={d?.satisfaction_level_quote}
          timestamps={d?.satisfaction_level_timestamps}
          hasVideo={hasVideo}
          onJumpToSeconds={onJumpToSeconds}
        />
      </>
    );
  }

  if (tile.kind === "positive") {
    return (
      <>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <MetricScalePill label="Importance" value={d?.positive_importance} metricKey="importance" scheme="green" />
        </Box>
        <DrawerField label="Positive category" value={d?.positive_category} multiline={false} />
        <DrawerField label="Why this importance score?" value={d?.positive_importance_justification} />
        <QuoteBlock
          quote={d?.positive_importance_quote}
          timestamps={d?.positive_timestamps}
          hasVideo={hasVideo}
          onJumpToSeconds={onJumpToSeconds}
        />
      </>
    );
  }

  if (tile.kind === "drawback") {
    return (
      <>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {/* Drawbacks: importance uses RED gradient */}
          <MetricScalePill label="Importance" value={d?.drawback_importance} metricKey="importance" scheme="red" />
        </Box>
        <DrawerField label="Drawback category" value={d?.drawback_category} multiline={false} />
        <DrawerField label="Why this importance score?" value={d?.drawback_importance_justification} />
        <QuoteBlock
          quote={d?.drawback_importance_quote}
          timestamps={d?.drawback_timestamps}
          hasVideo={hasVideo}
          onJumpToSeconds={onJumpToSeconds}
        />
      </>
    );
  }

  if (tile.kind === "still_used" || tile.kind === "not_used") {
    return (
      <>
        <DrawerField label="Situation category" value={d?.situation_category} multiline={false} />
        <QuoteBlock
          quote={d?.quote}
          timestamps={d?.timestamp}
          hasVideo={hasVideo}
          onJumpToSeconds={onJumpToSeconds}
        />
      </>
    );
  }

  if (tile.kind === "previous_solution") {
    return (
      <>
        <DrawerField label="Previous solution category" value={d?.previous_solution_category} multiline={false} />

        {asArray(d?.reasons_not_good_enough).length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
              Why wasn’t it good enough?
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {asArray(d?.reasons_not_good_enough).map((r, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {normalizeOptionalValue(r?.reason_not_good_enough) || "—"}
                  </Typography>

                  <DrawerField
                    label="Reason category"
                    value={r?.reason_not_good_enough_category}
                    multiline={false}
                  />

                  <QuoteBlock
                    quote={r?.reason_not_good_enough_quote}
                    timestamps={r?.reason_not_good_enough_timestamp}
                    hasVideo={hasVideo}
                    onJumpToSeconds={onJumpToSeconds}
                  />
                </Paper>
              ))}
            </Stack>
          </>
        ) : null}
      </>
    );
  }

  if (tile.kind === "trigger") {
    return <DrawerField label="Trigger category" value={d?.trigger_category} multiline={false} />;
  }

  if (tile.kind === "considered_solution") {
    const reasons = asArray(d?.reasons_not_chosen);
    return (
      <>
        <DrawerField label="Considered solution category" value={d?.considered_solution_category} multiline={false} />

        {reasons.length ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
              Reasons not chosen
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {reasons.map((r, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "space-between" }}>
                    {normalizeOptionalValue(r?.reason_not_chosen_category) ? (
                      <Chip size="small" variant="outlined" label={normalizeOptionalValue(r?.reason_not_chosen_category)} />
                    ) : (
                      <span />
                    )}

                    <MetricScalePill
                      label="Importance"
                      value={r?.reason_not_chosen_importance}
                      metricKey="importance"
                      scheme="green"
                    />
                  </Box>

                  <Typography variant="body2" sx={{ fontWeight: 900, mt: 0.75 }}>
                    {normalizeOptionalValue(r?.reason_not_chosen) || "—"}
                  </Typography>

                  <DrawerField
                    label="Why this importance score?"
                    value={r?.reason_not_chosen_importance_justification}
                  />

                  <QuoteBlock
                    quote={r?.reason_not_chosen_quote}
                    timestamps={r?.reason_not_chosen_timestamps}
                    hasVideo={hasVideo}
                    onJumpToSeconds={onJumpToSeconds}
                  />
                </Paper>
              ))}
            </Stack>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            No “reasons not chosen” were captured for this considered solution.
          </Typography>
        )}
      </>
    );
  }

  if (tile.kind === "not_chosen_reason") {
    return (
      <>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <MetricScalePill
            label="Importance"
            value={d?.reason_not_chosen_importance}
            metricKey="importance"
            scheme="green"
          />
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
          Context
        </Typography>
        <DrawerField label="Considered solution" value={d?.considered_solution} />
        <DrawerField label="Considered solution category" value={d?.considered_solution_category} multiline={false} />

        <Divider sx={{ my: 1.5 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
          Reason not chosen
        </Typography>

        <DrawerField label="Reason category" value={d?.reason_not_chosen_category} multiline={false} />
        <DrawerField label="Why this importance score?" value={d?.reason_not_chosen_importance_justification} />

        <QuoteBlock
          quote={d?.reason_not_chosen_quote}
          timestamps={d?.reason_not_chosen_timestamps}
          hasVideo={hasVideo}
          onJumpToSeconds={onJumpToSeconds}
        />
      </>
    );
  }

  if (tile.kind === "jtbd") {
    return <JtbdDetail d={d} hasVideo={hasVideo} onJumpToSeconds={onJumpToSeconds} />;
  }

  if (tile.kind === "insight") {
    const scheme = insightSchemeFromType(d?.insight_type);
    return (
      <>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
          <MetricScalePill
            label="Intensity"
            value={d?.insight_sentiment_score}
            metricKey="sentiment"
            scheme={scheme}
          />
          {normalizeOptionalValue(d?.insight_product_area) ? (
            <Chip size="small" variant="outlined" label={normalizeOptionalValue(d?.insight_product_area)} />
          ) : null}
        </Box>

        <DrawerField label="Category" value={d?.insight_category} multiline={false} />
        <DrawerField label="Summary" value={d?.insight_summary} />
        <DrawerField label="Type" value={d?.insight_type} multiline={false} />
        <DrawerField label="Sentiment overall" value={d?.insight_sentiment_overall} multiline={false} />
        <DrawerField label="Sentiment expressed" value={d?.insight_sentiment_expressed} multiline={false} />
        <DrawerField label="Why this intensity score?" value={d?.insight_sentiment_justification} />

        <QuoteBlock
          quote={d?.insight_quote}
          timestamps={d?.insight_timestamps}
          hasVideo={hasVideo}
          onJumpToSeconds={onJumpToSeconds}
        />
      </>
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      No detail renderer found for this tile.
    </Typography>
  );
}

/**
 * -------------------------
 * Main component
 * -------------------------
 */

export default function CustomerStorySection({ story, hasVideo, onJumpToSeconds }) {
  const tiles = useMemo(() => buildTiles(story || {}), [story]);

  const [open, setOpen] = useState(false);
  const [activeTile, setActiveTile] = useState(null);

  const openTile = useCallback((tile) => {
    setActiveTile(tile);
    setOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setOpen(false);
    setActiveTile(null);
  }, []);

  const hasBuying =
    tiles.chosenSolution.length ||
    tiles.positives.length ||
    tiles.drawbacks.length ||
    tiles.stillUsed.length ||
    tiles.notUsed.length ||
    tiles.previousAndTrigger.length ||
    tiles.consideredSolutions.length;

  const hasJobs = tiles.jobs.length > 0;
  const hasInsights = tiles.insights.length > 0;

  // Insights buckets: Positives / Negatives / Wish List, and Misc separate section
  const insightsBuckets = useMemo(() => {
    const buckets = {
      Positives: [],
      Negatives: [],
      "Wish List": [],
      Miscellaneous: [],
    };

    tiles.insights.forEach((t) => {
      const typeRaw = normalizeOptionalValue(t?.detail?.insight_type);
      let bucket = "Miscellaneous";
      if (typeRaw === "Positives") bucket = "Positives";
      else if (typeRaw === "Negatives") bucket = "Negatives";
      else if (typeRaw === "Feature Request" || typeRaw === "Wish List") bucket = "Wish List";
      else if (typeRaw === "Miscellaneous") bucket = "Miscellaneous";

      buckets[bucket].push({ ...t, _bucket: bucket });
    });

    // Sort each by intensity DESC (nulls last)
    Object.keys(buckets).forEach((k) => {
      buckets[k].sort((a, b) => {
        const av = safeNumber(a?.detail?.insight_sentiment_score);
        const bv = safeNumber(b?.detail?.insight_sentiment_score);
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return bv - av;
      });
    });

    return buckets;
  }, [tiles.insights]);

  // Usage contexts: combine into single grid (3-col) and color the pill red if "No longer used"
  const usageTiles = useMemo(() => {
    const still = tiles.stillUsed.map((t) => ({ ...t, _usageType: "Still used" }));
    const not = tiles.notUsed.map((t) => ({ ...t, _usageType: "No longer used" }));
    return [...still, ...not];
  }, [tiles.stillUsed, tiles.notUsed]);

  // Previous / Trigger tiles
  const prevTile = useMemo(
    () => tiles.previousAndTrigger.find((t) => t.kind === "previous_solution") || null,
    [tiles.previousAndTrigger]
  );
  const triggerTile = useMemo(
    () => tiles.previousAndTrigger.find((t) => t.kind === "trigger") || null,
    [tiles.previousAndTrigger]
  );

  return (
    <Box>
      {/* Buying Behaviors */}
      {hasBuying ? (
        <Box>
          <SectionTitle
            title="Buying Behaviors"
            subtitle="Click any tile to view full details, justifications, and clickable quote timestamps."
          />

          {tiles.chosenSolution.length ? (
            <>
              <SubsectionTitle title="Chosen solution" />
              <TileGrid>
                {tiles.chosenSolution.map((t) => (
                  <SummaryTile
                    key={t.id}
                    title={t.title}
                    metrics={[
                      <MetricScalePill
                        key="sat"
                        label="Satisfaction"
                        value={t.metrics.satisfaction}
                        metricKey="satisfaction_level"
                        scheme="green"
                      />,
                    ]}
                    onClick={() => openTile(t)}
                  />
                ))}
              </TileGrid>
            </>
          ) : null}

          {tiles.positives.length ? (
            <>
              <SubsectionTitle title="Positives" />
              <TileGrid>
                {tiles.positives.map((t) => (
                  <SummaryTile
                    key={t.id}
                    title={t.title}
                    metrics={[
                      <MetricScalePill
                        key="imp"
                        label="Importance"
                        value={t.metrics.importance}
                        metricKey="importance"
                        scheme="green"
                      />,
                    ]}
                    onClick={() => openTile(t)}
                  />
                ))}
              </TileGrid>
            </>
          ) : null}

          {tiles.drawbacks.length ? (
            <>
              <SubsectionTitle title="Drawbacks" />
              <TileGrid>
                {tiles.drawbacks.map((t) => (
                  <SummaryTile
                    key={t.id}
                    title={t.title}
                    metrics={[
                      <MetricScalePill
                        key="imp"
                        label="Importance"
                        value={t.metrics.importance}
                        metricKey="importance"
                        scheme="red"
                      />,
                    ]}
                    onClick={() => openTile(t)}
                  />
                ))}
              </TileGrid>
            </>
          ) : null}

          {/* Usage contexts */}
          {usageTiles.length ? (
            <>
              <SubsectionTitle
                title="Usage contexts"
                subtitle="More readable titles (taller tiles) while keeping a 3-column layout."
              />
              <TileGrid>
                {usageTiles.map((t) => {
                  const isNotUsed = t._usageType === "No longer used";
                  return (
                    <SummaryTile
                      key={t.id}
                      title={t.title}
                      lineClamp={4}
                      minLinesHeight={72}
                      chips={[
                        <Chip
                          key="usageType"
                          size="small"
                          variant="outlined"
                          label={t._usageType}
                          sx={
                            isNotUsed
                              ? {
                                  borderColor: "error.main",
                                  color: "error.main",
                                  "& .MuiChip-label": { color: "error.main" },
                                }
                              : undefined
                          }
                        />,
                      ]}
                      onClick={() => openTile(t)}
                    />
                  );
                })}
              </TileGrid>
            </>
          ) : null}

          {/* Previous solution + Trigger (separate headers) */}
          {prevTile || triggerTile ? (
            <>
              <SubsectionTitle title="Previous solutions" />
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  alignItems: "start",
                }}
              >
                {prevTile ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                      Previous solution
                    </Typography>
                    <SummaryTile title={prevTile.title} onClick={() => openTile(prevTile)} />
                  </Box>
                ) : null}

                {triggerTile ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                      Trigger for wanting something better
                    </Typography>
                    <SummaryTile title={triggerTile.title} onClick={() => openTile(triggerTile)} />
                  </Box>
                ) : null}
              </Box>
            </>
          ) : null}

          {/* Considered solutions: solution tile should NOT be full width; make it same width as reasons */}
          {tiles.consideredSolutions.length ? (
            <>
              <SubsectionTitle title="Considered solutions" subtitle="Solution tile + reasons not chosen (same tile width)." />

              <Stack spacing={2}>
                {tiles.consideredSolutions.map((group, idx) => {
                  const sol = group.solutionTile;
                  const reasons = group.reasonTiles || [];
                  const reasonCount = reasons.length;

                  return (
                    <Paper key={sol.id || idx} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <TileGrid>
                        {/* Solution tile now lives inside the grid -> same width as reasons */}
                        <SummaryTile
                          title={sol.title}
                          chips={[
                            <Chip key="count" size="small" variant="outlined" label={`${reasonCount} reason(s)`} />,
                          ]}
                          onClick={() => openTile(sol)}
                        />

                        {reasons.map((r) => (
                          <SummaryTile
                            key={r.id}
                            title={r.title}
                            metrics={[
                              <MetricScalePill
                                key="imp"
                                label="Importance"
                                value={r.metrics.importance}
                                metricKey="importance"
                                scheme="green"
                              />,
                            ]}
                            onClick={() => openTile(r)}
                          />
                        ))}
                      </TileGrid>

                      {!reasons.length ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                          No “reasons not chosen” captured.
                        </Typography>
                      ) : null}
                    </Paper>
                  );
                })}
              </Stack>
            </>
          ) : null}
        </Box>
      ) : null}

      {/* Jobs to be done */}
      {hasJobs ? (
        <Box sx={{ mt: hasBuying ? 3 : 0 }}>
          <SectionTitle
            title="Jobs To Be Done"
            subtitle="Tiles show JTBD + importance/satisfaction. Click a job for full detail (with tabs)."
          />

          <TileGrid>
            {tiles.jobs.map((t) => (
              <SummaryTile
                key={t.id}
                title={t.title}
                metrics={[
                  <MetricScalePill key="imp" label="Importance" value={t.metrics.importance} metricKey="importance" scheme="green" />,
                  <MetricScalePill key="sat" label="Satisfaction" value={t.metrics.satisfaction} metricKey="satisfaction" scheme="green" />,
                ]}
                chips={t.chips || []}
                onClick={() => openTile(t)}
              />
            ))}
          </TileGrid>
        </Box>
      ) : null}

      {/* Insights */}
      {hasInsights ? (
        <Box sx={{ mt: 3 }}>
          <SectionTitle
            title="Insights"
            subtitle="Positives / Negatives / Wish List in 3 columns. Miscellaneous is shown below."
          />

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              alignItems: "start",
            }}
          >
            {[
              { key: "Positives", title: "Positives" },
              { key: "Negatives", title: "Negatives" },
              { key: "Wish List", title: "Wish List" },
            ].map((col) => (
              <Box key={col.key}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                  {col.title}
                </Typography>

                {insightsBuckets[col.key]?.length ? (
                  <Stack spacing={1.5}>
                    {insightsBuckets[col.key].map((t) => {
                      const scheme = insightSchemeFromType(t?.detail?.insight_type, t?._bucket);
                      return (
                        <SummaryTile
                          key={t.id}
                          title={t.title}
                          metrics={[
                            <MetricScalePill
                              key="int"
                              label="Intensity"
                              value={t.metrics.intensity}
                              metricKey="sentiment"
                              scheme={scheme}
                            />,
                          ]}
                          chips={t.chips || []} // only product area chip
                          onClick={() => openTile(t)}
                        />
                      );
                    })}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Box>
            ))}
          </Box>

          {insightsBuckets.Miscellaneous?.length ? (
            <>
              <SubsectionTitle title="Miscellaneous" />
              <TileGrid>
                {insightsBuckets.Miscellaneous.map((t) => (
                  <SummaryTile
                    key={t.id}
                    title={t.title}
                    metrics={[
                      <MetricScalePill
                        key="int"
                        label="Intensity"
                        value={t.metrics.intensity}
                        metricKey="sentiment"
                        scheme="green"
                      />,
                    ]}
                    chips={t.chips || []}
                    onClick={() => openTile(t)}
                  />
                ))}
              </TileGrid>
            </>
          ) : null}
        </Box>
      ) : null}

      {!hasBuying && !hasJobs && !hasInsights ? (
        <Typography variant="body2" color="text.secondary">
          No Customer Story data available.
        </Typography>
      ) : null}

      {/* Drawer */}
        <Drawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        ModalProps={{
            // ✅ prevents browser scroll-jump caused by focus restoration
            disableRestoreFocus: true,

            // optional (usually helps reduce weird focus behavior)
            disableAutoFocus: true,
            disableEnforceFocus: true,
            keepMounted: true,
        }}
        PaperProps={{
            sx: {
            width: { xs: "100%", sm: 520 },
            p: 2,
            },
        }}
        >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            Details
          </Typography>

          <IconButton onClick={closeDrawer} aria-label="Close details">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ my: 2 }} />

        {normalizeOptionalValue(activeTile?.title) ? (
          <Typography variant="body1" sx={{ fontWeight: 900, whiteSpace: "pre-wrap" }}>
            {normalizeOptionalValue(activeTile?.title)}
          </Typography>
        ) : null}

        {hasVideo ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
            Tip: click any ▶ timestamp chip to jump the video to that moment.
          </Typography>
        ) : null}

        <Box sx={{ mt: 1.5 }}>
          <DetailView tile={activeTile} hasVideo={hasVideo} onJumpToSeconds={onJumpToSeconds} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Button variant="outlined" onClick={closeDrawer}>
          Close
        </Button>
      </Drawer>
    </Box>
  );
}
